import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  type AcquisitionRouteMetrics,
  type ExperimentMetrics,
  formatRate,
  formatRevenue,
  isPaidVariant,
  type OfferMetrics,
  variantLabel,
} from "../experiments/experimentClient";
import { buildConversionSteps, offerLabel } from "./monetizationModel";

export function ConversionFunnel({ metrics }: { metrics: ExperimentMetrics }) {
  const steps = buildConversionSteps(metrics);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Conversion funnel</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2 md:grid-cols-4 xl:grid-cols-7">
        {steps.map((step, index) => (
          <div key={step.label} className="rounded-lg border bg-muted/20 p-3">
            <p className="text-xs text-muted-foreground">
              {index + 1}. {step.label}
            </p>
            <p className="mt-1 text-2xl font-semibold">{step.count}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {step.nextRate === null
                ? "Completed purchases"
                : `${formatRate(step.nextRate)} to next step`}
            </p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function VariantComparison({
  variants,
}: {
  variants: ExperimentMetrics[];
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>By variant</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Variant</TableHead>
              <TableHead>Assigned</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Paid</TableHead>
              <TableHead>Paid orders</TableHead>
              <TableHead>Assigned → paid</TableHead>
              <TableHead>Net revenue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {variants.map((variant) => (
              <TableRow key={variant.variant}>
                <TableCell>
                  <Badge
                    variant={
                      isPaidVariant(variant.variant) ? "default" : "secondary"
                    }
                  >
                    {variantLabel(variant.variant)}
                  </Badge>
                </TableCell>
                <TableCell>{variant.assigned_contacts}</TableCell>
                <TableCell>{variant.echo_started_contacts}</TableCell>
                <TableCell>{variant.paid_contacts}</TableCell>
                <TableCell>{variant.paid_orders}</TableCell>
                <TableCell>
                  {formatRate(variant.assignment_to_paid_pct ?? null)}
                </TableCell>
                <TableCell>
                  {formatRevenue(variant.net_revenue_minor, variant.currency)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function AcquisitionPerformance({
  title,
  routes,
  currency,
}: {
  title: string;
  routes: AcquisitionRouteMetrics[];
  currency: string | null;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Route</TableHead>
              <TableHead>Assigned</TableHead>
              <TableHead>Paid</TableHead>
              <TableHead>Paid orders</TableHead>
              <TableHead>Assigned → paid</TableHead>
              <TableHead>Revenue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {routes.map((route) => (
              <TableRow key={route.acquisition_surface}>
                <TableCell className="capitalize">
                  {route.acquisition_surface}
                </TableCell>
                <TableCell>{route.assigned_contacts}</TableCell>
                <TableCell>{route.paid_contacts}</TableCell>
                <TableCell>{route.paid_orders}</TableCell>
                <TableCell>
                  {formatRate(route.assignment_to_paid_pct)}
                </TableCell>
                <TableCell>
                  {formatRevenue(route.revenue_minor, currency)}
                </TableCell>
              </TableRow>
            ))}
            {!routes.length ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center text-muted-foreground"
                >
                  No assignments with an acquisition route yet.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function PackagePerformance({ offers }: { offers: OfferMetrics[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Package performance</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Package</TableHead>
              <TableHead>Selected</TableHead>
              <TableHead>Paid orders</TableHead>
              <TableHead>Selection → paid</TableHead>
              <TableHead>Revenue</TableHead>
              <TableHead>Echoes granted</TableHead>
              <TableHead>Internal credits</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {offers.map((offer) => (
              <TableRow key={offer.offer_id}>
                <TableCell>
                  <p className="font-medium">{offerLabel(offer.offer_id)}</p>
                  <code className="text-xs text-muted-foreground">
                    {offer.offer_id}
                  </code>
                </TableCell>
                <TableCell>{offer.selected_contacts}</TableCell>
                <TableCell>{offer.paid_orders}</TableCell>
                <TableCell>{formatRate(offer.selection_to_paid_pct)}</TableCell>
                <TableCell>
                  {formatRevenue(offer.revenue_minor, offer.currency)}
                </TableCell>
                <TableCell>{offer.echoes_granted}</TableCell>
                <TableCell>{offer.internal_credits_granted}</TableCell>
              </TableRow>
            ))}
            {!offers.length ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-muted-foreground"
                >
                  No package selections yet.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        <p className="mt-3 text-xs text-muted-foreground">
          Internal credits are an operator-only accounting unit (100 per Echo)
          and are never included in customer-facing WhatsApp or Stripe copy.
        </p>
      </CardContent>
    </Card>
  );
}
